import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Skeleton } from "@mui/material";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import { useNavigate } from "react-router-dom";
import DateConverter from "@heartinz/date_and_time_processor";

import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import handleError from "../../../constants/handleError";
import Messages from "../../../constants/Messages";

import ViewStockArrival from "./ViewStockArrival";
import { useAuth } from "../../../context/AuthProvider";
import ReactPaginate from "react-paginate";
import { usePermission } from "../../../context/PermissionContext";
import useKeyboardTableNavigation from "../../../constants/useKeyboardTableNavigation";
import Table from "../../../constants/Table";
import { formatCurrencyFun } from "../../../utils/formatCurrencyFunc";
import { RefreshIcon, SearchIcon, ShowIcon } from "../../../constants/Icons";

const StockArrived = ({ isActive }) => {
  const [isPreLoading, setPreLoading] = useState(true);
  const [isStockData, setStockData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedStockId, setSelectedStockId] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const { globelPopupOpen } = usePermission();
  const containerRef = useRef(null);
  const [isStockModalOpen, setStockModalOpen] = useState(false);
  const [isData, setStockViewData] = useState(false);
  const searchProductRef = useRef(null);
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const { isUser } = useAuth();
  const perPage = 10;

  const nativeDateTime = (DateTime) => {
    const nativeDateTime = DateConverter.convertToTimeZone(
      DateTime,
      isUser?.timezone,
    );
    const isoDateTime = DateConverter.ISOToDisplayFormat(nativeDateTime);
    return `${isoDateTime.date}`;
  };

  const handleArrivalList = async (page = 0) => {
    setPreLoading(true);
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(
        `module/stocktransfer/arrival?pageno=${page + 1}&limit=${perPage}`,
      );
      const results = response.data.results || [];
      setStockData(results);
      setFilteredData(results);
      if (results.length === 0) {
        setTotalPages(0);
        setTotalItems(0);
        setCurrentPage(0);
      } else {
        setTotalPages(response.data.totalPages || 0);
        setTotalItems(response.data.totalItems || 0);
      }
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setPreLoading(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const filtered = isStockData.filter(
      (item) =>
        item.transferfrom.title?.toLowerCase().includes(query) ||
        item.transferfrom.email?.toLowerCase().includes(query) ||
        item.transferno?.toLowerCase().includes(query),
    );
    setFilteredData(filtered);
  };

  const handleViewAccept = async (stockId) => {
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(
        `module/stocktransfer/arrival/${stockId}`,
      );
      return response.data.results;
    } catch (error) {
      handleError(error, showAlert);
      return null;
    }
  };

  const handleView = async (item) => {
    try {
      const data = await handleViewAccept(item._id);
      if (data) {
        setStockViewData(data);
        setStockModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching view data:", error);
    } finally {
    }
  };

  const handleClose = () => {
    setStockModalOpen(false);
    setStockViewData(null);
  };

  useEffect(() => {
    handleArrivalList(currentPage);
  }, [currentPage]);

  const handleRefresh = async () => {
    await handleArrivalList(currentPage);
  };

  const getColor = (status) => {
    switch (status.toLowerCase()) {
      case "main":
        return "bg-label-orange";
      case "sub":
        return "bg-label-peach";
      case "hub":
        return "bg-label-night";
      default:
        return "bg-label-secondary";
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "bg-label-warning";
      case "accepted":
        return "bg-label-success";
      default:
        return "bg-label-secondary";
    }
  };

  const getText = (status) => {
    switch (status?.toUpperCase()) {
      case "PENDING":
        return "Pending";
      case "ACCEPTED":
        return "Accepted";
      case "RECEIVED":
        return "Transfered";
      default:
        return status;
    }
  };

  const handleAccept = async (
    transferid,
    transferitemid,
    acceptqty,
    isSecondaryUnit,
  ) => {
    // console.log(
    //   "Accepting stock with data:",
    //   transferid,
    //   transferitemid,
    //   acceptqty,
    //   isSecondaryUnit,
    // );
    try {
      if (!checkNetwork()) return;
      const data = {
        // transferid: transferid,
        transferitemid: transferitemid,
        quantity: acceptqty,
        isSecondaryUnit: isSecondaryUnit,
      };
      const stockResp = await AxiosInstance.patch(
        `module/stocktransfer/accepted/${transferid}`,
        data,
      );
      setStockViewData(stockResp.data.results);
      showAlert("success", Messages.SUCCESS.STOCK_ACCEPTED);

      setStockData((prevData) =>
        prevData.map((item) =>
          item._id === transferid
            ? { ...item, status: stockResp.data.results.status }
            : item,
        ),
      );
      setFilteredData((prevData) =>
        prevData.map((item) =>
          item._id === transferid
            ? { ...item, status: stockResp.data.results.status }
            : item,
        ),
      );
      return stockResp.data;
    } catch (error) {
      handleError(error, showAlert);
      return false;
    }
  };

  useKeyboardTableNavigation({
    isActive,
    modals: {
      isStockModalOpen,
      globelPopupOpen,
    },
    onEnter: handleView,
  });

  useEffect(() => {
    if (!isActive) return;

    // small timeout ensures DOM is visible after tab switch
    const timer = setTimeout(() => {
      searchProductRef.current?.focus();
    }, 0);

    return () => clearTimeout(timer);
  }, [isActive]);

  return (
    <>
      <div>
        <div className="page-wrapper">
          <div className="page-content">
            <div className="page-breadcrumb d-flex align-items-center mb-3 gap-2">
              <div className="pb-1">
                <button
                  type="button"
                  className="btn btn-sm btn-light p-1 shadow-none"
                  onClick={handleRefresh}
                  disabled={isPreLoading}
                  title="Refresh list"
                >
                  <RefreshIcon iClass={`${isPreLoading ? "bx-spin" : ""}`}/>
                </button>
              </div>
              <div className="breadcrumb-title pe-3">Stock Arrival</div>
            </div>

            <div className="row">
              <div className="col">
                <div className="card radius-10 mb-0">
                  <div className="card-body px-0">
                    <div className="row px-3">
                      <div className="col-lg-5 me-auto">
                        <div className="ms-auto position-relative">
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search transfer by transferno, name, email..."
                              value={searchQuery}
                              onChange={handleSearch}
                              ref={searchProductRef}
                            />
                            <button className="input-group-text">
                              <SearchIcon/>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div ref={containerRef}>
                      <Table
                        loading={isPreLoading}
                        data={filteredData}
                        onRowClick={(item) => handleView(item)}
                        columns={[
                          {
                            label: "S.No",
                            render: (_, index) => <>{index + 1}</>,
                          },
                          {
                            label: "Transfer No",
                            render: (item) => (
                              <span className="badge rounded-pill bg-label-sun">
                                {item.transferno}
                              </span>
                            ),
                          },
                          {
                            label: "Transfer Date",
                            render: (item) => (
                              <span className="text-heading fw-medium text-truncate">
                                {
                                  DateConverter.ISOToDisplayFormat(
                                    DateConverter.convertToTimeZone(
                                      item.transferdate,
                                    ),
                                  ).date
                                }
                              </span>
                            ),
                          },
                          {
                            label: "Transfer From",
                            render: (item) => (
                              <span className="text-heading fw-medium text-truncate">
                                <span
                                  className={`badge rounded-pill ${getColor(
                                    item.transferfrom?.stype || "",
                                  )}`}
                                >
                                  {item.transferfrom.stype}
                                </span>{" "}
                                - {item.transferfrom.title}
                                <div>
                                  <small>{item.transferfrom.email}</small>
                                </div>
                              </span>
                            ),
                          },
                          {
                            label: "Transfer Items",
                            render: (item) => <>{item.totalItems} Item(s)</>,
                          },
                          {
                            label: "Amount",
                            render: (item) => (
                              <span className="text-heading fw-medium text-truncate text-right">
                                {formatCurrencyFun(item?.grandtotal)}
                              </span>
                            ),
                          },
                          {
                            label: "Status",
                            render: (item) =>
                              item.status && (
                                <div className="no-print position-relative">
                                <div className=" d-flex justify-content-center align-items-center">
                                <span
                                  className={`stock-status-badge badge rounded-pill ${getStatusColor(
                                    item.status,
                                  )}`}
                                >
                                  {getText(item.status)}
                                </span>
                                <div className="order-actions order-actions3 opacity-0 ">
                                <button
                                    type="button"
                                    className="btn-label-primary shadow-none me-2"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleView(item);
                                    }}
                                  >
                                    <ShowIcon/>
                                  </button>
                                  </div>
                                  </div>
                                  </div>
                              ),
                          },
                          // {
                          //   label: "Actions",
                          //   render: (item) => (
                          //     <div className="order-actions opacity-0 opacity-md-100">
                          //       <button
                          //         type="button"
                          //         className="btn-label-primary shadow-none me-2"
                          //         onClick={(e) => {
                          //           e.stopPropagation();
                          //           handleView(item);
                          //         }}
                          //       >
                          //         <i className="bx bx-show"></i>
                          //       </button>
                          //     </div>
                          //   ),
                          // },
                        ]}
                      />
                    </div>
                    {totalPages > 0 && (
                      <div className="d-flex justify-content-center mt-3">
                        <ReactPaginate
                          previousLabel={"← Previous"}
                          nextLabel={"Next →"}
                          breakLabel={"..."}
                          onPageChange={({ selected }) => {
                            setPreLoading(true);
                            setCurrentPage(selected);
                          }}
                          pageRangeDisplayed={5}
                          pageCount={totalPages}
                          containerClassName="pagination"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {isStockModalOpen && (
              <ViewStockArrival
                data={isData}
                onClose={handleClose}
                onAccept={handleAccept}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default StockArrived;
